export enum AppArtStatus {
  draft = 'draft',
  submitted = 'submitted',
  modelsReady = 'models_ready',
  changesRequested = 'changes_requested',
  modelChosen = 'model_chosen',
  published = 'published',
  /** Rede que já tem arte, feita antes ou fora deste fluxo. */
  legacy = 'legacy',
}

export enum AppArtBucket {
  logo = 'logo',
  stationPhoto = 'station_photo',
  /** Mockups gerados a partir do material. A ★ aqui é o modelo escolhido. */
  model = 'model',
  /**
   * A placa de posto: a peça quadrada afixada na bomba, com o QR e o código de
   * abastecimento. A ★ aqui é a placa vigente da rede.
   *
   * NÃO é arte do aplicativo — não entra no build e não participa do fluxo de
   * aprovação (`models_ready` → `model_chosen`). Por isso tem seção própria na
   * tela, separada dos modelos.
   */
  stationPlate = 'station_plate',
}

export const STATUS_LABEL: Record<string, string> = {
  [AppArtStatus.draft]: 'Rascunho',
  [AppArtStatus.submitted]: 'Na fila da geração',
  [AppArtStatus.modelsReady]: 'Modelos gerados',
  [AppArtStatus.changesRequested]: 'Nova rodada pedida',
  [AppArtStatus.modelChosen]: 'Modelo escolhido',
  [AppArtStatus.published]: 'Arte definida',
  [AppArtStatus.legacy]: 'Arte fora deste fluxo',
};

/** Ordem do stepper. `changes_requested` é um desvio de models_ready, não um passo. */
export const STATUS_STEPS = [
  AppArtStatus.draft,
  AppArtStatus.submitted,
  AppArtStatus.modelsReady,
  AppArtStatus.modelChosen,
  AppArtStatus.published,
];

/** Rótulo curto do passo — o do chip não cabe embaixo da bolinha do stepper. */
export const STEP_LABEL: Record<string, string> = {
  [AppArtStatus.draft]: 'Material',
  [AppArtStatus.submitted]: 'Geração',
  [AppArtStatus.modelsReady]: 'Modelos',
  [AppArtStatus.modelChosen]: 'Escolha',
  [AppArtStatus.published]: 'Arte definida',
};

/**
 * Em que passo o stepper para, por status.
 *
 * `changes_requested` NÃO está em STATUS_STEPS: um `indexOf` cru devolvia -1 e
 * o stepper acendia "Rascunho" — a tela dizia que a rede estava no começo do
 * fluxo quando ela já tinha modelos gerados e uma rodada nova pedida.
 */
export const STEP_INDEX: Record<string, number> = {
  [AppArtStatus.draft]: 0,
  [AppArtStatus.submitted]: 1,
  [AppArtStatus.modelsReady]: 2,
  [AppArtStatus.changesRequested]: 2,
  [AppArtStatus.modelChosen]: 3,
  [AppArtStatus.published]: 4,
  // `legacy` está FORA do fluxo — a tela nem desenha o stepper nesse status.
  [AppArtStatus.legacy]: 0,
};

export interface StatusGuide {
  /** O que esse status significa — estado, não instrução. */
  what: string;
  /** A próxima ação, dita com o nome do botão que a executa. */
  next: string;
  severity: 'info' | 'warning' | 'success';
}

/**
 * O texto que a tela mostra em cada status.
 *
 * Uma frase para o estado e uma para o próximo passo, sempre nessa ordem e
 * sempre citando o botão que faz a coisa acontecer. Quem opera está com o
 * cliente esperando no WhatsApp: a tela precisa responder "e agora?" sem que
 * ninguém tenha que lembrar do fluxo inteiro.
 */
export const STATUS_GUIDE: Record<string, StatusGuide> = {
  [AppArtStatus.draft]: {
    what: 'Material aberto para edição. Nada foi para a geração ainda.',
    next: 'Junte as logos, as imagens do posto, as cores e o briefing e clique em “Enviar para geração”. É preciso pelo menos uma logo.',
    severity: 'info',
  },
  [AppArtStatus.submitted]: {
    what: 'Na fila do gerador. O material está travado para não mudar debaixo de quem está desenhando.',
    next: 'Rode o gerador na sua máquina — o botão “Gerar modelos” dá o comando pronto. Ele sobe os modelos e move o status sozinho. Para corrigir o material antes disso, use “Voltar para rascunho”.',
    severity: 'info',
  },
  [AppArtStatus.modelsReady]: {
    what: 'Os modelos da última rodada estão no card “Modelos gerados”.',
    next: 'Mostre para o cliente. Escolheu: marque “Escolher este modelo” e clique em “Confirmar modelo escolhido”. Não gostou: “Pedir nova rodada” devolve a rede para o gerador.',
    severity: 'success',
  },
  [AppArtStatus.changesRequested]: {
    what: 'O cliente pediu outra rodada. Os modelos anteriores continuam guardados — nada é apagado.',
    next: 'Rode o gerador de novo (botão “Gerar modelos”): ele sobe uma rodada nova e volta o status para “Modelos gerados”.',
    severity: 'warning',
  },
  [AppArtStatus.modelChosen]: {
    what: 'Modelo aprovado pelo cliente. O material e a escolha estão travados.',
    next: 'Confira que é essa a arte que vai para o app e feche com “Marcar arte como definida”.',
    severity: 'success',
  },
  // Aqui NÃO se fala em publicação: o que acontece nas lojas não passa por
  // esta tela, então afirmar "está no ar" seria alegar o que não se sabe.
  [AppArtStatus.published]: {
    what: 'Arte definida e pronta para gerar os assets do aplicativo.',
    next: 'Nada a fazer por aqui. Se o cliente voltar atrás, “Reabrir” traz de volta para modelo escolhido.',
    severity: 'success',
  },
  [AppArtStatus.legacy]: {
    what: 'Rede com arte feita antes ou fora deste fluxo. Nenhum material foi coletado aqui.',
    next: 'Nada a fazer. Se um dia a rede pedir arte nova, “Começar arte nova” recomeça do rascunho.',
    severity: 'info',
  },
};

export const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success'> = {
  [AppArtStatus.draft]: 'default',
  [AppArtStatus.submitted]: 'info',
  [AppArtStatus.modelsReady]: 'info',
  [AppArtStatus.changesRequested]: 'warning',
  [AppArtStatus.modelChosen]: 'success',
  [AppArtStatus.published]: 'success',
  [AppArtStatus.legacy]: 'default',
};

/**
 * O que o arquivo é dentro do bucket `model`.
 *
 * O gerador sobe três arquivos por modelo: a folha (o mockup que se mostra ao
 * cliente, onde mora a ★ de "modelo escolhido") e os dois artboards crus, que
 * são os assets de produção e o fundo que a tela usa para desenhar o mockup ao
 * vivo. `null` é upload manual, que vale como folha.
 */
export enum AppArtFileVariant {
  sheet = 'sheet',
  welcome = 'welcome',
  header = 'header',
}

/**
 * A receita que o gerador grava na linha da FOLHA de cada modelo.
 *
 * O gerador entrega imagens; o mockup visual é montado aqui. Sem a receita a
 * tela chuta: `showWelcomeLabel` ficava sempre `true` e o mockup desenhava o
 * BEM-VINDO! e o logo do RN por cima do logo que o asset já tinha gravado.
 */
export interface ArtRecipe {
  modelKey: string;
  nome: string;
  estilo: 'foto' | 'degrade' | 'ilustrado';
  identidade: 'nenhuma' | 'logo' | 'completa';
  paleta: 'marca' | 'acento' | 'neutro';
  app: {
    /** `appShowWelcomeLabel`. Modelo que grava o logo no asset exige `false`. */
    showWelcomeLabel: boolean;
    /** O que o PNG já traz desenhado — o mockup não deve repetir por cima. */
    gravadoNoAsset: string[];
    /** % da largura da tela que o logo ocupa no asset. */
    logoWidth: number | null;
  };
  cores: {
    /**
     * O que está NO PIXEL. Mudar isso na tela não muda o fundo: a arte já foi
     * rasterizada. Serve para avisar, não para configurar.
     */
    gravadas: { brand: string; brand2: string; escurecimento: number; paleta: string };
    /**
     * Config real do app, ajustável. `welcomeTextColor` está aqui de propósito
     * e é sempre branco: está cravado em `Welcome/index.js` e nenhuma prop
     * muda — a tela não deve oferecer controle para ele.
     */
    chrome: {
      headerLabelColor: string;
      welcomeTextColor: string;
      signUpButtonColor: string;
      signInButtonColor: string;
    };
  };
  /* `origem` diz QUEM desenhou: `agente` (e `ajuste`) deixam HTML de autor;
     `sorteio` é o template e não pode ser corrigido por texto. */
  receita?: { origem?: string } & Record<string, unknown>;
  /* O HTML de autor dos dois artboards, que é o insumo do ajuste. Só existe
     nas rodadas subidas depois de o gerador passar a gravá-lo — rodada mais
     velha tem `recipe` e não tem isto, e o ajuste dela é recusado. */
  interno?: { welcome?: string; header?: string };
  avaliacao?: Record<string, unknown>;
  ficha?: string;
}

/** Um retângulo do gabarito, em pixel e em fração do lado da peça. */
export interface PlateRect {
  x: number; y: number; w: number; h: number;
  fracao: { x: number; y: number; w: number; h: number };
}

/**
 * A geometria da placa de posto — o contrato de quem compõe o QR e o código.
 *
 * `area` é o retângulo em que TODO pixel é branco puro, medido no PNG e não
 * lido do CSS: `border-radius`, borda e sombra interna encolhem a área
 * aproveitável, e a caixa do elemento esconde isso. `quadrado` é o maior
 * quadrado centrado nela, que é o que o QR quer. `usar` diz qual dos dois
 * consumir — não interprete `caixa`, ela é só referência.
 *
 * `fracao` existe para renderizar em qualquer tamanho: multiplique pelo lado
 * que estiver desenhando e o retângulo sai certo, sem tabela de conversão.
 */
export interface PlateRecipe {
  gerador: string;
  motor: 'agente' | 'deterministico';
  canvas: { w: number; h: number };
  slots: {
    qr: { usar: 'quadrado'; quadrado: PlateRect; area: PlateRect; caixa: PlateRect;
      aproveitamento: number; observacao: string };
    codigo: { usar: 'area'; area: PlateRect; caixa: PlateRect; aproveitamento: number;
      fonteSugerida: { px: number; peso: number; espacamentoPx: number }; observacao: string };
  };
  areas: { logo: PlateRect; cttx: PlateRect };
  logo: { aspecto: number; casa: boolean; chapaInjetada: { cor: string; motivo: string } | null;
    cores: { cor: string; peso: number }[];
    contraste: { cor: string; contraFundo: number; contraBranco: number }[] };
  cores: Record<string, string>;
}

/**
 * Uma bomba do mapa de pista, como `/station-qrcodes` devolve — é o ForecourtMap
 * da ConectTec, a mesma consulta da aba "Mapa de Pista" do posto.
 *
 * `qrcode` é o código de abastecimento REAL da bomba: o que o frentista lê e o
 * que o app aceita digitado ou lido do QR (7 dígitos).
 */
export interface ForecourtFuelPoint {
  id: number;
  stationNumericId: number | string;
  qrcode: string;
  fuelPointNumber: number | string;
  hoses: { hoseNumber: number | null; fuel?: { name?: string } | null }[];
}

/** O mínimo do posto que a placa usa: nome e os IDs numéricos da ConectTec. */
export interface PlateStation {
  id: string;
  name: string;
  numericIds: number[] | null;
  /** Posto em implantação ainda está inativo e é quando mais se imprime placa. */
  isActive?: boolean;
}

export interface AppArtFile {
  id: string;
  chainId: string;
  bucket: AppArtBucket;
  fileUrl: string;
  previewUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  byteSize: number | null;
  width: number | null;
  height: number | null;
  hasAlpha: boolean | null;
  pageCount: number | null;
  isPrimary: boolean;
  displayOrder: number;
  /** Rodada de geração — só no bucket `model`. */
  generationRound: number | null;
  caption: string | null;
  variant: AppArtFileVariant | null;
  /** Qual modelo da rodada (A, B, C, D1…) — liga a folha aos seus artboards. */
  modelKey: string | null;
  warnings: string[] | null;
  /** Só na folha, e só em rodadas geradas depois da coluna existir. */
  /**
   * No bucket `model`, é a receita do modelo (`ArtRecipe`). No `station_plate`,
   * é a GEOMETRIA do gabarito (`PlateRecipe`) — quem lê discrimina pelo
   * `bucket` do próprio arquivo.
   */
  recipe: ArtRecipe | PlateRecipe | null;
  createdAt: string;
}

/* ── Estreitando `recipe` ────────────────────────────────────────────────

   `recipe` guarda coisas diferentes em buckets diferentes, e o TypeScript não
   deixa isso passar batido — o que é bom: sem estas duas funções, um componente
   de modelo leria `recipe.nome` numa linha de placa e receberia `undefined` em
   silêncio.

   O discriminante é `slots`, que só a geometria da placa tem. */
export const isPlateRecipe = (r: ArtRecipe | PlateRecipe | null | undefined): r is PlateRecipe =>
  !!r && 'slots' in r;

/** A receita do MODELO, ou null se a linha não for de modelo. */
export const artRecipeOf = (file?: AppArtFile | null): ArtRecipe | null => (
  file?.recipe && !isPlateRecipe(file.recipe) ? file.recipe : null
);

/** A geometria da PLACA, ou null se a linha não for de placa. */
export const plateRecipeOf = (file?: AppArtFile | null): PlateRecipe | null => (
  isPlateRecipe(file?.recipe) ? file.recipe : null
);

export interface AppArt {
  id: string;
  chainId: string;
  chainName: string;
  exists: boolean;
  status: AppArtStatus;
  appName: string | null;
  appNameStore: string | null;
  appNameInside: string | null;
  slogan: string | null;
  color: string | null;
  secondaryColor: string | null;
  headerLabelColor: string | null;
  signUpButtonColor: string | null;
  signInButtonColor: string | null;
  socials: Record<string, string | null> | null;
  briefing: string | null;
  submittedAt: string | null;
  publishedAt: string | null;
  updatedAt: string | null;
  files: AppArtFile[];
}

export interface AppArtListRow {
  id: string;
  chainId: string;
  chainName: string;
  chainLogo: string | null;
  status: AppArtStatus;
  logoCount: number;
  photoCount: number;
  updatedAt: string | null;
  submittedAt: string | null;
}
